import { useMemo } from 'react';
import { Box } from '@mui/material';
import { Form, Formik } from 'formik';
import { startOfDay } from 'date-fns';
import { toast } from 'react-toastify';
import { LoadingButton } from '@mui/lab';
import InputGroup from '../form/InputGroup';
import {
  EmployeeToBusiness,
  FormikForm,
  InputRowType,
  LoadedUser,
} from '../../interfaces';
import {
  getTimeBlockForm,
  RepeatFrequency,
  TimeBlockFormValues,
} from '../../types/availability';
import { isUserAdminOrManager } from '../../utils/global/helper';
import useUser from '../../hooks/global/useUser';
import useCreateUpdateTimeBlock from '../../hooks/availability/useCreateUpdateTimeBlock';
import {
  invalidateBatchTimeBlocks,
  invalidateBatchTimeBlocksEmployee,
} from '../../services/employee';
import { getFullName } from '../../helpers/global';
import { EmployeesResourceMap, ResourceBlock } from '../../types/employees';
import { getTimeBlockData } from '../../helpers/employees';
import { CalendarSlotSelection } from '../../types/calendar';
import useBusinessWebRedesign from '../../hooks/businesses/useBusinessWebRedesign';
import { ModalHeaderBadge } from '../modal/FullPageModal';

import '../form/form.css';

interface Props {
  onClose: () => void;
  selectedArtists?: string[];
  employeesResourceMap?: EmployeesResourceMap;
  existing?: {
    employee: EmployeeToBusiness;
    resourceBlock: ResourceBlock;
  };
  slotSelection?: CalendarSlotSelection | null;
}

function TimeBlockForm({
  onClose,
  existing,
  employeesResourceMap,
  selectedArtists,
  slotSelection,
}: Props) {
  const { user } = useUser<LoadedUser>();
  const { active: redesignActive } = useBusinessWebRedesign();

  const timeBlockForm = getTimeBlockForm(user);

  const { mutateAsync, isPending } = useCreateUpdateTimeBlock({
    onError: err => toast.error(err.message),
    onSuccess: () => {
      toast.success(
        `Successfully ${existing ? 'updated' : 'created'} time block`,
      );
      if (existing && selectedArtists) {
        invalidateBatchTimeBlocksEmployee({
          employeeId: existing.employee.employeeId,
          selectedArtists,
        });
      } else {
        invalidateBatchTimeBlocks();
      }
      onClose();
    },
  });

  const { name, start, end, isAllDay, startDateString, endDateString } =
    useMemo(
      () =>
        getTimeBlockData({
          employee: existing?.employee,
          employeesResourceMap,
          timeBlock: existing?.resourceBlock,
        }),
      [employeesResourceMap, existing?.employee, existing?.resourceBlock],
    );

  const repeats = !!existing?.resourceBlock.repeats;
  const repeat = repeats ? existing?.resourceBlock.repeat : null;
  // Slot-click prefills only apply when creating a new time block.
  const slot = existing ? null : slotSelection;
  const nullDate = null as unknown as Date;

  const form: FormikForm<TimeBlockFormValues> = {
    validationSchema: timeBlockForm,
    initialValues: {
      resourceBlockId: existing?.resourceBlock.id,
      employeeId: existing?.employee?.employeeId,
      name: name ?? '',
      repeats: (repeat?.frequency as RepeatFrequency) ?? '',
      interval: repeat?.interval ?? 1,
      date:
        !repeats && startDateString
          ? new Date(startDateString)
          : slot
          ? startOfDay(slot.start)
          : nullDate,
      dateRange:
        repeats && startDateString && endDateString
          ? [new Date(startDateString), new Date(endDateString)]
          : (null as unknown as TimeBlockFormValues['dateRange']),
      days:
        repeat?.frequency === RepeatFrequency.W
          ? repeat.weekdays?.split('').map(day => parseInt(day, 10)) ?? []
          : [],
      isAllDay,
      startTime: start ?? slot?.start ?? nullDate,
      endTime: end ?? slot?.end ?? nullDate,
      employeeIds: existing
        ? [existing.employee.employeeId]
        : slot?.employeeId
        ? [slot.employeeId]
        : [],
    },
    onSubmit: (vals, { setSubmitting }) => {
      try {
        mutateAsync({
          ...vals,
          employeeId: existing?.employee.employeeId,
          resourceBlockId: existing?.resourceBlock?.id,
        });
      } catch {
        // EMPTY
      } finally {
        setSubmitting(false);
      }
    },
  };

  return (
    <Box
      style={{
        width: '100%',
        margin: 'auto',
      }}>
      <Formik
        enableReinitialize
        validationSchema={form.validationSchema}
        initialValues={form.initialValues}
        onSubmit={form.onSubmit}>
        {({ isSubmitting, isValid, dirty, values }) => (
          <Form>
            {redesignActive && <ModalHeaderBadge label="Time block" />}
            <InputGroup
              name="Time Block"
              rows={
                [
                  [
                    {
                      label: 'Name',
                      name: 'name',
                      type: 'text',
                      placeholder: 'Enter a name for this time block',
                      disabled: isPending || isSubmitting,
                    },
                  ],
                  [
                    {
                      label: 'Repeats',
                      name: 'repeats',
                      type: 'select',
                      autoWidth: false,
                      options: [
                        {
                          label: 'Does not repeat',
                          value: '',
                        },
                        {
                          label: 'Repeats daily',
                          value: 'D',
                        },
                        {
                          label: 'Repeats weekly',
                          value: 'W',
                        },
                      ],
                      disabled: isPending || isSubmitting,
                    },
                  ],
                  values.repeats
                    ? [
                        {
                          label: 'Repeats Every',
                          name: 'interval',
                          type: 'select',
                          autoWidth: false,
                          options: Array.from(
                            { length: 10 },
                            (_, i) => i + 1,
                          ).map(num => ({
                            label: `Every ${num === 1 ? '' : `${num} `}${
                              values.repeats === 'D'
                                ? num === 1
                                  ? 'day'
                                  : 'days'
                                : num === 1
                                ? 'week'
                                : 'weeks'
                            }`,
                            value: num,
                          })),
                          disabled: isPending || isSubmitting,
                        } as InputRowType[number],
                      ]
                    : null,
                  [
                    {
                      label: !values?.repeats ? 'Blocked day' : 'Blocked from',
                      name: !values?.repeats ? 'date' : 'dateRange',
                      type: !values?.repeats ? 'date' : 'date-range',
                      minDate: startOfDay(new Date()),
                      disabled: isPending || isSubmitting,
                    },
                  ],
                  [
                    {
                      label: 'Start time',
                      name: 'startTime',
                      type: 'time',
                      disabled: isPending || isSubmitting || values?.isAllDay,
                      displayEmpty: values?.isAllDay,
                      minutesStep: 15,
                    },
                    {
                      label: 'End time',
                      name: 'endTime',
                      type: 'time',
                      disabled: isPending || isSubmitting || values?.isAllDay,
                      displayEmpty: values?.isAllDay,
                      minutesStep: 15,
                    },
                  ],
                  values.repeats && values.repeats === 'W'
                    ? [
                        {
                          label: 'Days',
                          name: 'days',
                          type: 'multiselect',
                          hideAllOption: true,
                          options: [
                            {
                              label: 'Sunday',
                              value: 0,
                            },
                            {
                              label: 'Monday',
                              value: 1,
                            },
                            {
                              label: 'Tuesday',
                              value: 2,
                            },
                            {
                              label: 'Wednesday',
                              value: 3,
                            },
                            {
                              label: 'Thursday',
                              value: 4,
                            },
                            {
                              label: 'Friday',
                              value: 5,
                            },
                            {
                              label: 'Saturday',
                              value: 6,
                            },
                          ],
                          allText: 'Every day of the week',
                          autoWidth: false,
                          placeholder: 'Select days',
                          disabled: isPending || isSubmitting,
                        } as InputRowType[number],
                      ]
                    : null,
                  [
                    {
                      label: 'All day',
                      name: 'isAllDay',
                      type: 'select',
                      autoWidth: false,
                      options: [
                        {
                          label: 'Yes',
                          value: true,
                        },
                        {
                          label: 'No',
                          value: false,
                        },
                      ],
                      disabled: isPending || isSubmitting,
                    },
                  ],
                  !!user && isUserAdminOrManager(user)
                    ? [
                        {
                          label: 'Employees',
                          tooltip: existing
                            ? undefined
                            : 'Select the employees to create this time block for. Once created, time blocks will only be manageable by the specific employee.',
                          name: 'employeeIds',
                          type: 'multiselect',
                          autoWidth: false,
                          options:
                            user?.business?.employees?.map(e => ({
                              label:
                                e.displayName ||
                                (e.user ? getFullName(e.user) : ''),
                              value: e.employeeId,
                            })) ?? [],
                          disabled: !!existing || isPending || isSubmitting,
                          placeholder: 'Select employees',
                          allText: 'All employees',
                        },
                      ]
                    : null,
                ].filter(Boolean) as InputRowType[]
              }
            />
            <LoadingButton
              type="submit"
              loading={isSubmitting || isPending}
              disabled={isSubmitting || isPending || !isValid || !dirty}
              variant="contained"
              className="primary">
              {existing ? 'Update' : 'Create Time Block'}
            </LoadingButton>
          </Form>
        )}
      </Formik>
    </Box>
  );
}

export default TimeBlockForm;
